import { Company } from '@handover/shared-types';

export type CompanyAiAlertSeverity = 'info' | 'warning' | 'error';

export type CompanyAiState = {
  canUseAi: boolean;
  isTrial: boolean;
  isUsageLimitReached: boolean;
  isUsingGraceBuffer: boolean;
  usagePercent: number;
  totalCredits: number;
  remainingCredits: number;
  graceCreditsRemaining: number;
  alertSeverity: CompanyAiAlertSeverity | null;
  alertMessage: string | null;
};

const PAID_BILLING_STATUSES = ['active', 'past_due', 'manual', 'complimentary'];

export const getCompanyAiState = (company?: Company | null): CompanyAiState => {
  const billingStatus = String(company?.billing?.status || 'inactive');
  const stripeSubscriptionStatus = company?.billing?.stripeSubscriptionStatus;
  const aiUsage = company?.aiUsage;
  const totalCredits =
    Number(aiUsage?.includedCreditsMonthly || 0) + Number(aiUsage?.purchasedCredits || 0);
  const usedCredits = Number(aiUsage?.usedCredits || 0);
  const remainingCredits = Math.max(totalCredits - usedCredits, 0);
  const graceCreditsRemaining = Math.max(
    Number(aiUsage?.graceCredits || 0) - Number(aiUsage?.graceCreditsUsed || 0),
    0
  );
  const usagePercent =
    totalCredits > 0 ? Math.min(100, Math.round((usedCredits / totalCredits) * 100)) : 0;
  const isTrial = stripeSubscriptionStatus === 'trialing';
  const isPaidWorkspace = PAID_BILLING_STATUSES.includes(billingStatus);
  const isUsageLimitReached = remainingCredits <= 0 && graceCreditsRemaining <= 0;
  const isUsingGraceBuffer =
    remainingCredits <= 0 && Number(aiUsage?.graceCreditsUsed || 0) > 0 && graceCreditsRemaining >= 0;
  const aiEnabled = Boolean(aiUsage?.enabled);

  if (isTrial) {
    return {
      canUseAi: false,
      isTrial: true,
      isUsageLimitReached: false,
      isUsingGraceBuffer: false,
      usagePercent,
      totalCredits,
      remainingCredits,
      graceCreditsRemaining,
      alertSeverity: 'info',
      alertMessage: 'AI unlocks when this workspace upgrades to a paid plan ($50/month, unlimited users).',
    };
  }

  if (!isPaidWorkspace || !aiEnabled) {
    return {
      canUseAi: false,
      isTrial: false,
      isUsageLimitReached: false,
      isUsingGraceBuffer: false,
      usagePercent,
      totalCredits,
      remainingCredits,
      graceCreditsRemaining,
      alertSeverity: 'info',
      alertMessage: 'AI is available for active paid workspaces only.',
    };
  }

  if (isUsageLimitReached) {
    return {
      canUseAi: false,
      isTrial: false,
      isUsageLimitReached: true,
      isUsingGraceBuffer: false,
      usagePercent: 100,
      totalCredits,
      remainingCredits,
      graceCreditsRemaining,
      alertSeverity: 'error',
      alertMessage: 'AI usage has reached 100%. Buy more usage to keep AI features available.',
    };
  }

  if (isUsingGraceBuffer) {
    return {
      canUseAi: true,
      isTrial: false,
      isUsageLimitReached: false,
      isUsingGraceBuffer: true,
      usagePercent: 100,
      totalCredits,
      remainingCredits,
      graceCreditsRemaining,
      alertSeverity: 'warning',
      alertMessage: `You are using the AI grace buffer. ${graceCreditsRemaining} grace credit${graceCreditsRemaining === 1 ? '' : 's'} remain before AI pauses.`,
    };
  }

  if (usagePercent >= 80) {
    return {
      canUseAi: true,
      isTrial: false,
      isUsageLimitReached: false,
      isUsingGraceBuffer: false,
      usagePercent,
      totalCredits,
      remainingCredits,
      graceCreditsRemaining,
      alertSeverity: 'warning',
      alertMessage: `AI usage is at ${usagePercent}%. Consider buying more usage before AI pauses.`,
    };
  }

  if (usagePercent >= 50) {
    return {
      canUseAi: true,
      isTrial: false,
      isUsageLimitReached: false,
      isUsingGraceBuffer: false,
      usagePercent,
      totalCredits,
      remainingCredits,
      graceCreditsRemaining,
      alertSeverity: 'info',
      alertMessage: `AI usage is at ${usagePercent}% for this billing cycle.`,
    };
  }

  return {
    canUseAi: true,
    isTrial: false,
    isUsageLimitReached: false,
    isUsingGraceBuffer: false,
    usagePercent,
    totalCredits,
    remainingCredits,
    graceCreditsRemaining,
    alertSeverity: null,
    alertMessage: null,
  };
};
